import { isEmpty, isNil } from "@Utils";
import { DefaultOptionType } from "rc-select/lib/Select";

type NotArray<T> = T extends unknown[] ? never : T;

export const dateFormatList: string[] = ["DD/MM/YYYY", "DD/MM/YY"];

/**
 *
 * @param list
 * @param indexFn determines which key should be used
 * @param isUnique determines whether one value or an array of values should be returned if the key is not unique
 */
export function arrayToMapByKey<T extends Partial<T>, B extends undefined>(
  list: T[],
  indexFn: ((item: T) => string) | string,
  isUnique?: B,
  valueFn?: ((item: T) => T[]) | keyof T
): Map<string, T[]>;
export function arrayToMapByKey<T extends Partial<T>, B extends boolean>(
  list: T[],
  indexFn: ((item: T) => string) | string,
  isUnique?: B,
  valueFn?: (item: T) => T | T[]
): B extends true ? Map<string, T> : Map<string, T[]>;
export function arrayToMapByKey<T extends Partial<T>, B extends boolean, C extends keyof T>(
  list: T[],
  indexFn: ((item: T) => string) | string,
  isUnique?: B,
  valueFn?: C
): B extends true ? Map<string, T[C]> : Map<string, T[C][]>;
export function arrayToMapByKey<T extends Partial<T>>(
    list: T[],
    indexFn: ((item: T) => string) | string,
    isUnique = false,
    valueFn = undefined
): Map<string, T | T[]> | Map<string, T> {
    const map = new Map<string, T | T[]>();
    list?.forEach((record) => {
        const key = typeof indexFn === "function" ? indexFn(record) : indexFn;
        if (!key) return;
        const collection = map.get(key);
        const itemValue: T = valueFn && (valueFn === "function" ? valueFn(record) : record[valueFn]);
        const item: T = !isNil(itemValue) ? itemValue : record;

        if (isUnique) {
            map.set(key, item);
            return;
        }

        if (!collection) {
            map.set(key, [item]);
            return;
        }

        if (Array.isArray(collection)) {
            collection.push(item);
        }
    });
    return map;
}

export function uniqueArrayOfObjectsByKey<T extends Partial<Record<keyof T, unknown>>>(obj: T[], key: keyof T): T[] {
  const set = new Set();
  return obj.filter((value) => {
    if (set.has(value?.[key])) return false;
    set.add(value?.[key]);
    return true;
  });
}

export function sortObjectByKey<T extends Partial<Record<keyof T, unknown>>>(obj: T[], key: keyof T): T[] {
  return obj.sort((a, b) => {
    if (`${a?.[key]}`.toLowerCase() > `${b?.[key]}`.toLowerCase()) return 1;
    if (`${a?.[key]}`.toLowerCase() < `${b?.[key]}`.toLowerCase()) return -1;
    return 0;
  });
}

export const standardSearchFilter = (input: string, option: DefaultOptionType) =>
  `${option?.children}`.toLowerCase().includes(input.toLowerCase());


/**
 *
 * Recursively removes empty artefacts from an object
*/
export function removeEmptyItems<T>(obj: NotArray<T>): T {
  // TODO: needs more testing before being used with arrays
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([_, v]) => !isEmpty(v))
      .map(([k, v]) => [k, v === Object(v) ? removeEmptyItems<T>(v) : v])
  ) as T;
}

/**
 * Filters a tree node based on a search value.
 *
 * @param inputValue - The search string to filter by.
 * @param treeNode - The tree node to check against the filter.
 * @returns True if the node's title includes the search value (case-insensitive), false otherwise.
 * @remarks
 * - This function is used with the `filterTreeNode` prop of the Ant D TreeSelect component.
 * - It performs a case-insensitive search on the node's title.
 */
export const filterTreeNode = (inputValue: string, treeNode: Partial<DefaultOptionType>) => {
    return String(treeNode.title).toLowerCase().includes(inputValue.toLowerCase());
};

export interface IBuildTree<T> {
    data: T[];
    fields: {
        children?: ITreeItem[] | ((value: T) => ITreeItem[]);
        disabled?: boolean | ((value: T) => boolean);
        key?: keyof T | ((value: T) => string);
        prefix?: string;
        title: keyof T | ((value: T) => string);
        value: keyof T | ((value: T) => string);
    };
}

export interface ITreeItem {
  children?: ITreeItem[];
  disableCheckbox?: boolean;
  disabled?: boolean;
  key: string;
  title: string;
  label: string;
  value: string;
}

export function buildTree<T>({ data, fields }: IBuildTree<T>): ITreeItem[] {
  const prefix = fields.prefix ?? "";
  const key = fields.key || fields.value;
  const disabled = fields.disabled ?? false;
  return data?.map((datum: T) => {
    const title = typeof fields.title === "function" ? fields.title(datum) : datum[fields.title];
    const treeItem: ITreeItem = {
      disabled: typeof disabled === "function" ? disabled(datum) : disabled,
      key: `${prefix}${typeof key === "function" ? key(datum) : datum[key]}`,
      title: `${title}`,
      label: `${title}`, // some and trees use label instead of title so let's just add both
      value: `${prefix}${typeof fields.value === "function" ? fields.value(datum) : datum[fields.value]}`,
    };
    if (fields.children) {
      treeItem.children = typeof fields.children === "function" ? fields.children(datum) : fields.children;
    }

    if (treeItem?.children?.length === 0) {
      treeItem.disableCheckbox = true;
      // append empty if there are no nested trees
      treeItem.title = `${treeItem.title} (empty)`;
    }
    return treeItem;
  });
}

/**
 * Adds all the elements of an array into a string, separated by the specified separator string.
 * More performant than Array.join when working with complex datasets
*/
export const fastJoin = <T>(arr: T[], separator = ","): string => {
    return arr.reduce((acc, elem, index) => {
        const value = elem === null || elem === undefined ? "" : elem;
        return index === 0 ? `${value}` : `${acc}${separator}${value}`;
    }, "");
};

/**
 * Retrieves the value of a CSS variable.
 *
 * @param variableName - The name of the CSS variable to retrieve (including the leading '--').
 * @returns The value of the CSS variable.
 */
export const getCSSVariable = (variableName: string): string => {
  const value = getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
  if (!value) {
    return "";
  }
  return value;
};
